import { Bell, Download, Plus, Search, Settings, Sparkles, Trash2, Video } from 'lucide-react'
import { useState } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import {
  DialogContent,
  DialogRoot,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/Dialog'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { Toggle } from '@/components/ui/Toggle'
import { Tooltip } from '@/components/ui/Tooltip'

import { EmptyState } from '@/components/ds/EmptyState'
import { Logo } from '@/components/ds/Logo'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { StatusChip, type VideoStatus } from '@/components/ds/StatusChip'

import styles from './DesignSystemCatalog.module.scss'

const SORT_OPTIONS: ReadonlyArray<SegmentedOption<'recent' | 'ranking' | 'views'>> = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'ranking', label: 'Por ranking' },
  { value: 'views', label: 'Mais vistos' },
]

const STATUSES: VideoStatus[] = [
  'publicado',
  'agendado',
  'gerando',
  'rascunho',
  'falhou',
  'elegivel',
  'bloqueado',
]

export function DesignSystemCatalog() {
  const [sort, setSort] = useState<'recent' | 'ranking' | 'views'>('recent')
  const [toggleOn, setToggleOn] = useState(true)
  const [selectValue, setSelectValue] = useState<string>('')

  return (
    <div className={styles.page}>
      <PageHeader
          eyebrow="Design System"
          title="Prism — Catálogo de componentes"
          description="Referência viva de todos os tokens e componentes do design system. Tudo aqui bate pixel-perfect com o handoff do Claude Design."
          actions={
            <Button leadingIcon={<Plus />}>Novo</Button>
          }
        />

        {/* ---------- Brand ---------- */}
        <Section title="Logo" description="Variante mark (ícone) e full (ícone + wordmark).">
          <div className={styles.cluster}>
            <Logo variant="mark" size="sm" />
            <Logo variant="mark" size="md" />
            <Logo variant="mark" size="lg" />
            <Logo variant="full" size="sm" />
            <Logo variant="full" size="md" />
            <Logo variant="full" size="lg" />
          </div>
        </Section>

        {/* ---------- Colors ---------- */}
        <Section title="Cores" description="Tokens semânticos. Todos os componentes consomem via CSS vars.">
          <div className={styles.swatchGrid}>
            {[
              { name: 'brand-ignite', token: '--brand-ignite' },
              { name: 'brand-ignite-hover', token: '--brand-ignite-hover' },
              { name: 'brand-ignite-soft', token: '--brand-ignite-soft' },
              { name: 'brand-chartreuse', token: '--brand-chartreuse' },
              { name: 'bg', token: '--bg' },
              { name: 'bg-subtle', token: '--bg-subtle' },
              { name: 'surface', token: '--surface' },
              { name: 'surface-alt', token: '--surface-alt' },
              { name: 'surface-raised', token: '--surface-raised' },
              { name: 'border', token: '--border' },
              { name: 'border-strong', token: '--border-strong' },
              { name: 'fg', token: '--fg' },
              { name: 'fg-muted', token: '--fg-muted' },
              { name: 'fg-subtle', token: '--fg-subtle' },
              { name: 'success', token: '--success' },
              { name: 'warning', token: '--warning' },
              { name: 'danger', token: '--danger' },
              { name: 'info', token: '--info' },
            ].map((c) => (
              <div key={c.token} className={styles.swatch}>
                <div className={styles.swatchBox} style={{ background: `var(${c.token})` }} />
                <div className={styles.swatchLabel}>
                  <span className={styles.swatchName}>{c.name}</span>
                  <span className={styles.swatchToken}>{c.token}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ---------- Typography ---------- */}
        <Section title="Tipografia" description="Inter body, Space Grotesk display, JetBrains Mono para números e micro-labels.">
          <div className={styles.typeStack}>
            <div className={styles.typeDisplay}>Display 56 — Prism</div>
            <div className={styles.typeH1}>H1 40 — Backlog ranqueado</div>
            <div className={styles.typeH2}>H2 28 — Seção</div>
            <div className={styles.typeH3}>H3 22 — Subseção</div>
            <div className={styles.typeBody}>Body 14 — Inter é o corpo padrão de toda a UI. Parágrafos, descrições, labels de formulário.</div>
            <div className={styles.typeCaption}>Caption mono 11 — STEP 4/7</div>
            <div className={styles.typeCode}>Code 13 — 0.92 · 212k · 1.2M imp</div>
          </div>
        </Section>

        {/* ---------- Buttons ---------- */}
        <Section title="Button" description="Primary, secondary, ghost × sm, md. Estados: default, hover, disabled.">
          <div className={styles.componentBlock}>
            <div className={styles.label}>Primary</div>
            <div className={styles.cluster}>
              <Button size="md">Gerar vídeo</Button>
              <Button size="md" leadingIcon={<Plus />}>Novo projeto</Button>
              <Button size="md" trailingIcon={<Download />}>Baixar</Button>
              <Button size="sm">Pequeno</Button>
              <Button disabled>Disabled</Button>
            </div>
          </div>
          <div className={styles.componentBlock}>
            <div className={styles.label}>Secondary</div>
            <div className={styles.cluster}>
              <Button variant="secondary">Cancelar</Button>
              <Button variant="secondary" leadingIcon={<Settings />}>Configurações</Button>
              <Button variant="secondary" size="sm">Pequeno</Button>
              <Button variant="secondary" disabled>Disabled</Button>
            </div>
          </div>
          <div className={styles.componentBlock}>
            <div className={styles.label}>Ghost</div>
            <div className={styles.cluster}>
              <Button variant="ghost">Ignorar</Button>
              <Button variant="ghost" leadingIcon={<Sparkles />}>Sugestões</Button>
              <Button variant="ghost" size="sm">Pequeno</Button>
            </div>
          </div>
        </Section>

        <Section title="IconButton" description="Botões só-ícone com aria-label obrigatório.">
          <div className={styles.cluster}>
            <IconButton aria-label="Notificações"><Bell /></IconButton>
            <IconButton aria-label="Configurações" variant="outline"><Settings /></IconButton>
            <IconButton aria-label="Buscar" variant="ghost"><Search /></IconButton>
            <IconButton aria-label="Excluir" variant="outline" size="sm"><Trash2 /></IconButton>
            <IconButton aria-label="Disabled" disabled><Bell /></IconButton>
          </div>
        </Section>

        {/* ---------- Inputs ---------- */}
        <Section title="Input" description="Campo de texto com label mono opcional, ícones e estados.">
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="ds-i-1">Título</label>
              <Input id="ds-i-1" placeholder="Título do vídeo" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="ds-i-2">Buscar</label>
              <Input id="ds-i-2" placeholder="Buscar..." leadingIcon={<Search />} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="ds-i-3">Com erro</label>
              <Input id="ds-i-3" defaultValue="valor inválido" error />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="ds-i-4">Disabled</label>
              <Input id="ds-i-4" defaultValue="não editável" disabled />
            </div>
          </div>
        </Section>

        {/* ---------- Select ---------- */}
        <Section title="Select" description="Dropdown select (Radix) estilizado como um campo do design system.">
          <div className={styles.cluster}>
            <SelectRoot value={selectValue} onValueChange={setSelectValue}>
              <SelectTrigger aria-label="Canal">
                <SelectValue placeholder="Selecione um canal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug-da-realidade">Bug da Realidade</SelectItem>
                <SelectItem value="ceu-de-hoje">Céu de Hoje</SelectItem>
                <SelectItem value="super-forge">Super Forge Review</SelectItem>
                <SelectItem value="camisa-9">Camisa 9 Stats</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>
        </Section>

        {/* ---------- Chips ---------- */}
        <Section title="Chip" description="Chips genéricos (6 variantes semânticas).">
          <div className={styles.cluster}>
            <Chip>default</Chip>
            <Chip variant="primary">primary</Chip>
            <Chip variant="success">success</Chip>
            <Chip variant="warning">warning</Chip>
            <Chip variant="info">info</Chip>
            <Chip variant="danger">danger</Chip>
          </div>
        </Section>

        <Section title="StatusChip" description="Chip semântico para status de vídeo (mapeado para variantes do Prism).">
          <div className={styles.cluster}>
            {STATUSES.map((s) => (
              <StatusChip key={s} status={s} />
            ))}
          </div>
        </Section>

        {/* ---------- Avatar ---------- */}
        <Section title="Avatar" description="Avatar circular com inicial. Cor customizável por canal.">
          <div className={styles.cluster}>
            <Avatar name="Bruno" size="sm" />
            <Avatar name="Bruno" size="md" />
            <Avatar name="Bruno" size="lg" />
            <Avatar name="Céu" color="var(--info)" />
            <Avatar name="Super" color="var(--success)" />
            <Avatar name="Camisa" color="var(--warning)" />
          </div>
        </Section>

        {/* ---------- Toggle ---------- */}
        <Section title="Toggle" description="Switch acessível (Radix Switch).">
          <div className={styles.cluster}>
            <Toggle id="ds-t-1" label="Publicar automaticamente" checked={toggleOn} onCheckedChange={setToggleOn} />
            <Toggle id="ds-t-2" label="Desativado" disabled />
          </div>
        </Section>

        {/* ---------- Segmented ---------- */}
        <Section title="Segmented" description="Controle segmentado para sort/filtros.">
          <div className={styles.cluster}>
            <Segmented
              options={SORT_OPTIONS}
              value={sort}
              onValueChange={setSort}
              aria-label="Ordenação"
            />
          </div>
        </Section>

        {/* ---------- Tooltip ---------- */}
        <Section title="Tooltip" description="Tooltip acessível (Radix Tooltip).">
          <div className={styles.cluster}>
            <Tooltip content="Acesso rápido via atalho G">
              <Button variant="secondary" leadingIcon={<Video />}>Hover em mim</Button>
            </Tooltip>
            <Tooltip content="Apagar projeto" side="right">
              <IconButton aria-label="Apagar"><Trash2 /></IconButton>
            </Tooltip>
          </div>
        </Section>

        {/* ---------- DropdownMenu ---------- */}
        <Section title="DropdownMenu" description="Menu contextual com ícones, divisor, item perigoso.">
          <div className={styles.cluster}>
            <DropdownMenuRoot>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">Ações</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Projeto</DropdownMenuLabel>
                <DropdownMenuItem leadingIcon={<Download />} hint="⌘D">Baixar</DropdownMenuItem>
                <DropdownMenuItem leadingIcon={<Settings />}>Configurações</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem leadingIcon={<Trash2 />} danger>
                  Apagar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuRoot>
          </div>
        </Section>

        {/* ---------- Dialog ---------- */}
        <Section title="Dialog" description="Modal (Radix Dialog) com header, body, footer.">
          <div className={styles.cluster}>
            <DialogRoot>
              <DialogTrigger asChild>
                <Button variant="secondary">Abrir dialog</Button>
              </DialogTrigger>
              <DialogContent
                title="Apagar projeto?"
                description="Essa ação não pode ser desfeita. Os arquivos serão removidos."
                footer={
                  <>
                    <DialogClose asChild>
                      <Button variant="secondary">Cancelar</Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button>Apagar</Button>
                    </DialogClose>
                  </>
                }
              >
                <p className={styles.dialogText}>
                  Este é um exemplo de conteúdo dentro do modal. Use para confirmações,
                  formulários curtos ou mensagens importantes.
                </p>
              </DialogContent>
            </DialogRoot>
          </div>
        </Section>

        {/* ---------- EmptyState ---------- */}
      <Section title="EmptyState" padding="none" description="Estado vazio padrão do produto.">
        <EmptyState
          icon={<Video />}
          title="Nenhum vídeo ainda"
          description="Gere seu primeiro vídeo para começar. O backlog vai aparecer aqui."
          action={<Button leadingIcon={<Plus />}>Gerar primeiro vídeo</Button>}
        />
      </Section>
    </div>
  )
}
